import type { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { Spinner } from './Spinner';

type Variant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'marketing-primary'
  | 'marketing-secondary'
  | 'marketing-inverted'
  | 'social';

type Size = 'sm' | 'md' | 'lg' | 'xl' | 'icon-sm' | 'icon-md' | 'icon-lg';

type CommonProps = {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  loadingText?: ReactNode;
  children: ReactNode;
  className?: string;
  // Native `disabled` only exists on <button> — Link/anchor variants fake it below by blocking
  // the click and stripping keyboard focus, since a disabled CTA link is otherwise impossible.
  disabled?: boolean;
};

type AsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children' | 'disabled'> & { to?: undefined; href?: undefined };
type AsLink = CommonProps &
  Omit<LinkProps, 'className' | 'children'> & { to: LinkProps['to']; href?: undefined };
type AsAnchor = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'disabled'> & { href: string; to?: undefined };

type Props = AsButton | AsLink | AsAnchor;

const variantClasses: Record<Variant, string> = {
  primary: 'bg-accent text-black hover:bg-amber-400 disabled:opacity-50',
  secondary:
    'border-2 border-line bg-surface-raised text-text hover:border-line/80 hover:bg-surface disabled:opacity-50',
  ghost: 'border-2 border-transparent bg-transparent text-muted hover:border-line hover:text-text hover:bg-surface-raised disabled:opacity-50',
  danger:
    'bg-danger-bg text-danger hover:bg-danger hover:text-white disabled:opacity-50',
  'marketing-primary': 'bg-accent text-black font-black normal-case hover:bg-amber-400 disabled:opacity-50',
  'marketing-secondary':
    'bg-transparent text-white/70 font-medium normal-case hover:text-white disabled:opacity-50',
  'marketing-inverted': 'bg-black text-white font-black normal-case hover:bg-zinc-900 disabled:opacity-50',
  social: 'bg-white text-gray-800 font-semibold normal-case hover:bg-gray-50 disabled:opacity-60 rounded-xl',
};

const sizeClasses: Record<Size, string> = {
  sm: 'px-3 py-2 text-xs font-bold uppercase tracking-wide',
  md: 'px-4 py-3 text-xs font-bold uppercase tracking-wide',
  lg: 'px-6 py-3.5 text-sm font-bold uppercase tracking-wide',
  xl: 'px-8 py-5 text-base font-black normal-case',
  'icon-sm': 'h-9 w-9 p-0',
  'icon-md': 'h-10 w-10 p-0',
  'icon-lg': 'h-11 w-11 p-0',
};

function getClasses(variant: Variant, size: Size, className: string) {
  // A single `transition` (not `transition-colors`) so the hover color change and the active
  // press-scale below animate together instead of one silently winning over the other.
  return `inline-flex items-center justify-center text-center gap-2 rounded-xl transition duration-150 active:scale-[0.97] outline-none focus-visible:ring-1 focus-visible:ring-accent ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;
}

export function Button(props: Props) {
  const {
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingText,
    children,
    className = '',
    ...rest
  } = props as CommonProps & Record<string, unknown>;

  if (import.meta.env.DEV && size.startsWith('icon') && !rest['aria-label']) {
    console.warn('Button: icon-only buttons (size="icon-*") should have an aria-label.');
  }

  const classes = getClasses(variant, size, className);
  const content = loading && loadingText ? loadingText : children;
  const spinner = loading && <Spinner size="sm" />;

  if (rest.to !== undefined) {
    const { to, href: _href, disabled, ...linkRest } = rest as unknown as LinkProps & { href?: undefined; disabled?: boolean };
    return (
      <Link
        to={to}
        className={`${classes} ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        onClick={disabled ? (e) => e.preventDefault() : linkRest.onClick}
        {...linkRest}
      >
        {spinner}
        {content}
      </Link>
    );
  }

  if (rest.href !== undefined) {
    const { href, to: _to, disabled, ...anchorRest } = rest as unknown as AnchorHTMLAttributes<HTMLAnchorElement> & {
      to?: undefined;
      disabled?: boolean;
    };
    return (
      <a
        href={href}
        className={`${classes} ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        onClick={disabled ? (e) => e.preventDefault() : anchorRest.onClick}
        {...anchorRest}
      >
        {spinner}
        {content}
      </a>
    );
  }

  const { to: _to, href: _href, ...buttonRest } = rest as unknown as ButtonHTMLAttributes<HTMLButtonElement> & {
    to?: undefined;
    href?: undefined;
  };
  return (
    <button {...buttonRest} disabled={buttonRest.disabled || loading} className={classes}>
      {spinner}
      {content}
    </button>
  );
}
